import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useEffect, useState } from "react";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import { Build, Download } from "@mui/icons-material";
import { getInspectionReportById, getInspectionReportPdf } from "api/inspection";
import HeaderCard from "components/DataTable/HeaderCard";
import ImagePreviewModal from "components/ImagePreviewModal";
import ListingImageCol from "components/ListingImageCol";
import Loading from "components/Loading";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useGlobalStore from "store/useGlobalStore";
import { parentRoute } from ".";

const InspectionDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setPrevSelectedRow } = useGlobalStore();
    const [isLoading, setIsLoading] = useState(false);
    const [inspectionData, setInspectionData] = useState(null);
    const [inspectionDataImages, setInspectionDataImages] = useState(null);
    const [showImagePreview, setShowImagePreview] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(null);
    const id = Number(location.pathname.split('/')[2]);

    useEffect(() => {
        const fetchUserData = async () => {

            if (!id || isNaN(id)) {
                navigate(parentRoute, { state: location.state });
                return;
            }
            const inspectionId = id;

            setIsLoading(true);
            try {
                const { data: inspectionData } = await getInspectionReportById(inspectionId);
                if (inspectionData) {
                    setInspectionData(inspectionData);
                    setInspectionDataImages(inspectionData.inspection_checklist.map(item => item.img_filename).filter(Boolean));
                }
            } catch (error) {
                toast.error("Failed to find inspection data");
                navigate(parentRoute, { state: location.state });
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserData();
    }, [location.pathname, navigate, id, location.state]);


    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={4} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <HeaderCard
                                title="Inspection Details"
                                showBackButton={true}
                                onBackClick={() => { navigate(parentRoute, { state: location.state }); setPrevSelectedRow(id, parentRoute) }}
                            />

                            <MDBox sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', p: 3, gap: 2 }}>
                                <MDTypography variant="h5" component="div">
                                    {inspectionData?.type?.name}  Inspection Report
                                </MDTypography>
                                <MDButton
                                    startIcon={<Download />}
                                    variant="outlined"
                                    color="primary"
                                    disabled={!inspectionData}
                                    onClick={() => {
                                        getInspectionReportPdf(inspectionData.id, inspectionData.report_id)
                                    }}
                                >
                                    Download as PDF
                                </MDButton>

                            </MDBox>
                            <MDBox px={3} pb={5}>
                                {isLoading ? (
                                    <Loading loading={isLoading} />
                                ) : inspectionData ? (
                                    <>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <MDTypography variant="caption"><b>Report ID:</b></MDTypography>
                                                <MDTypography variant="body2">{inspectionData.report_id}</MDTypography>
                                            </Grid>
                                            <Grid item xs={12} md={8} >
                                                <MDTypography variant="caption"><b>Event Location:</b></MDTypography>
                                                <MDTypography variant="body2">{inspectionData.event?.name} ({inspectionData.event?.location}, {inspectionData.event?.state?.name})</MDTypography>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <MDTypography variant="caption"><b>Category:</b></MDTypography>
                                                <MDTypography variant="body2">{inspectionData.category?.name}</MDTypography>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <MDTypography variant="caption"><b>Ride:</b></MDTypography>
                                                <MDTypography variant="body2">{inspectionData.ride?.name}</MDTypography>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <MDTypography variant="caption"><b>Date of Issue:</b></MDTypography>
                                                <MDTypography variant="body2">{new Date(inspectionData.created_at).toLocaleString()}</MDTypography>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <MDTypography variant="caption"><b>Issued By:</b></MDTypography>
                                                <MDTypography variant="body2">{inspectionData.user?.full_name} ({inspectionData.user?.role?.name})</MDTypography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <MDTypography variant="caption"><b>Remark:</b></MDTypography>
                                                <MDTypography variant="body2">{inspectionData.remark || '-'}</MDTypography>
                                            </Grid>
                                        </Grid>
                                        {inspectionData.inspection_checklist?.length ? (
                                            <Grid container mt={2}>
                                                <Grid item xs={12}>
                                                    <MDBox sx={{ overflowX: 'auto' }}>
                                                        <table className="inspection-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>#</th>
                                                                    <th>Inspection Checklist</th>
                                                                    <th>Status</th>
                                                                    <th>Comments</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {inspectionData.inspection_checklist.map((item, index) => {
                                                                    // Status color logic
                                                                    let statusColor = "#757575";
                                                                    if (item.status?.name?.toLowerCase() === "pass") statusColor = "#4caf50";
                                                                    else if (item.status?.name?.toLowerCase() === "fail") statusColor = "#f44336";
                                                                    else if (item.status?.name?.toLowerCase() === "skip") statusColor = "black";
                                                                    const isFailed = item.status?.name?.toLowerCase() === 'fail';
                                                                    return (
                                                                        <tr key={item.id}>
                                                                            <td className="inspection-index"><MDTypography variant="body2">{index + 1}</MDTypography></td>
                                                                            <td className="inspection-checklist">
                                                                                {/* <ListingImageCol imgUrl={item.checklist_item?.img_filename} name={item.checklist_item?.name} variant="body2" color="inherit" onClick={() => setShowImagePreview(item?.checklist_item?.img_filename)} noMaxWidth /> */}
                                                                                <MDTypography variant="body2" color="inherit" onClick={() => setShowImagePreview(item?.checklist_item?.img_filename)}>
                                                                                    {item.checklist_item?.name}
                                                                                </MDTypography>
                                                                            </td>
                                                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                                                <MDBox display="flex" alignItems="center" gap={0.5}>
                                                                                    <MDTypography variant="body2" sx={{ color: statusColor, fontWeight: 500 }}>
                                                                                        {item.status?.name}
                                                                                    </MDTypography>
                                                                                    {isFailed && (
                                                                                        <Tooltip title="Create Work Order">
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                color="error"
                                                                                                onClick={() => {
                                                                                                    navigate('/work-orders/add-work-order', {
                                                                                                        state: {
                                                                                                            fromInspection: true,
                                                                                                            // need to be same with workOrders/add.js report info structure (share UI)
                                                                                                            inspection_checklist_item: {
                                                                                                                id: item.id,
                                                                                                                inspection_report: inspectionData,
                                                                                                                checklist_item: item.checklist_item,
                                                                                                            },
                                                                                                            rideId: inspectionData.ride?.id,
                                                                                                            returnPathname: location.pathname,
                                                                                                        }
                                                                                                    });
                                                                                                }}
                                                                                                sx={{ ml: 0.5 }}
                                                                                            >
                                                                                                <Build fontSize="inherit" color="action" />
                                                                                            </IconButton>
                                                                                        </Tooltip>
                                                                                    )}
                                                                                </MDBox>
                                                                            </td>
                                                                            <td className="inspection-comments">
                                                                                <MDTypography variant="body2">{item.comment ?? ""}</MDTypography>
                                                                                <ListingImageCol
                                                                                    imgUrl={item?.img_filename}
                                                                                    onClick={() => {
                                                                                        setCurrentImageIndex(index);
                                                                                    }}
                                                                                />
                                                                            </td>
                                                                            {/* No separate action column; inline icon used above */}
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </MDBox>
                                                </Grid>
                                            </Grid>
                                        ) : (
                                            <MDTypography variant="body2">No checklist items found.</MDTypography>
                                        )}
                                    </>
                                ) : (
                                    <MDTypography variant="body2">No inspection data found.</MDTypography>
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
                {currentImageIndex !== null && (
                    <ImagePreviewModal
                        images={inspectionData.inspection_checklist?.map(item => item?.img_filename)}
                        comments={inspectionData.inspection_checklist?.map(item => item?.comment)}
                        index={currentImageIndex}
                        open={currentImageIndex !== null}
                        onClose={() => setCurrentImageIndex(null)}
                        enableNavigation={true}
                        onPrev={() => setCurrentImageIndex(i => (i > 0 ? i - 1 : i))}
                        onNext={() => setCurrentImageIndex(i => (i < inspectionDataImages.length - 1 ? i + 1 : i))}
                    />
                )}
            </MDBox>
        </DashboardLayout>
    );
};

export default InspectionDetails;
